'use client';
import { useState } from 'react';
import { ChangeStepFormEvent } from '../multi-step-form/multi-step-form.model';
import layoutStyles from '../multi-step-form/layout.module.css';

export type CardItemOption = {
  value: string;
  label: string;
  icon?: React.ReactNode;
};
type Props = {
  items: CardItemOption[];
  columns?: number;
  columnWidth?: number;
  columnHeight?: number;
  selectedItem?: string;
  name: string;
  onItemChange?: (item: string) => void;
  onChange: (value: ChangeStepFormEvent) => void;
  displayTextInput?: boolean;
  textInputValue?: string;
  textInputPlaceHolder?: string;
};

/**
 * Provides a grid layout for items that can be selected applying specific styles.
 * @param items List of items to be displayed.
 * @param columns Number of columns to be displayed. (default 3)
 * @param columnWidth Width of each column in px. (default 143)
 * @param columnHeight Height of each column in px. (default 108)
 * @param selectedItem The item that is selected.
 * @param onItemChange Callback function that is called when an item is selected.
 */

export const CardItemsSelection = ({
  items,
  selectedItem,
  columns = 2,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  columnWidth = 200,
  columnHeight = 108,
  name,
  onItemChange,
  onChange,
  displayTextInput,
  textInputValue,
  textInputPlaceHolder,
}: Props) => {
  const [item, setItem] = useState(() => selectedItem ?? '');
  const [inputValue, setInputValue] = useState(() => textInputValue ?? '');

  const handleItemChange = (item: string) => {
    onChange({ target: { name, value: item, textInputValue: '' } });
    setInputValue('');
    if (onItemChange) {
      onItemChange(item);
    }
    setItem(() => item);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(() => e.target.value);
    onChange({ target: { name, value: item, textInputValue: e.target.value } });
  };

  return (
    <div className={`${layoutStyles.item} flex flex-col gap-3 lg:min-w-[52%]`}>
      <div className={`grid gap-2.5 md:grid-cols-${columns} grid-cols-1`}>
        {items.map((option) => (
          <div
            className={`outline border-none outline-1 bg-white outline-secondary p-4 rounded cursor-pointer transition-colors duration-200 flex w-full items-center justify-center flex-col text-center ${
              option.value === item ? 'card-item-selected' : ''
            }`}
            style={{
              height: `${columnHeight}px`,
            }}
            key={option.value}
            onClick={() => handleItemChange(option.value)}
          >
            {option.icon} <span className="text-sm">{option.label}</span>
          </div>
        ))}
      </div>
      {displayTextInput && (
        <input
          type="text"
          id={name}
          name={name}
          value={inputValue}
          placeholder={textInputPlaceHolder}
          onChange={handleTextInputChange}
          className="w-full p-4 rounded-md focus:outline-2 focus:outline-primary-green placeholder:text-[#707071] outline-[1.5px] outline-[#cfcfd0] outline"
        />
      )}
    </div>
  );
};
